import { X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { addressLabel, dedupeAddresses, isAddress, splitAddresses } from '../../lib/emailRecipients';

/**
 * The To / Cc / Bcc rows above an email composer.
 *
 * Addresses are chips rather than free text, and one becomes a chip only once it
 * parses. That is the whole point: the send path refuses a malformed address
 * (services/outbound.js), and a box that lets an agent type `bob@acme` and press
 * send answers with a validation error under an emptied composer. Here the
 * address simply refuses to become a chip, next to the cursor that typed it,
 * while the words are still there.
 *
 * A paste is split on commas, semicolons and newlines — the three shapes an
 * address list arrives in — but never on a space, or `Bob Smith <b@x>` would
 * come apart into three things. Blur commits, because a typed address left
 * uncommitted when the agent clicks Send is an address they believe they added.
 */
function Field({
  label,
  values,
  onChange,
  autoFocus,
  placeholder,
  leading,
}: {
  label: string;
  values: string[];
  onChange: (next: string[]) => void;
  autoFocus?: boolean;
  placeholder?: string;
  /**
   * A chip the field does not own, drawn before its own. The new-message window
   * puts the picked CONTACT here: they are the first recipient and also the
   * person the conversation will belong to, so the field can neither remove
   * them nor treat them as one of its addresses.
   */
  leading?: ReactNode;
}) {
  const [typed, setTyped] = useState('');
  const [rejected, setRejected] = useState(false);

  /** Commit whatever is typed. Returns false when it was there but unusable, so
   *  Enter can keep it in the box rather than swallowing it. */
  const commit = (raw: string): boolean => {
    const parts = splitAddresses(raw);
    if (!parts.length) return true;
    const good = parts.filter(isAddress);
    if (good.length) onChange(dedupeAddresses([...values, ...good]));
    const ok = good.length === parts.length;
    setTyped(ok ? '' : parts.filter((part) => !isAddress(part)).join(', '));
    setRejected(!ok);
    return ok;
  };

  return (
    <div className={`cmp-rcpt-field${rejected ? ' invalid' : ''}`}>
      <span className="cmp-rcpt-label">{label}</span>
      <div className="cmp-rcpt-chips">
        {leading}
        {values.map((value) => (
          <span key={value} className="cmp-rcpt-chip" title={value}>
            {addressLabel(value)}
            <button
              type="button"
              title={`Remove ${value}`}
              onClick={() => onChange(values.filter((entry) => entry !== value))}
            >
              <X size={10} />
            </button>
          </span>
        ))}
        <input
          value={typed}
          // biome-ignore lint/a11y/noAutofocus: the panel opens because the agent
          // asked to address somebody; the caret belongs in the field they opened.
          autoFocus={autoFocus}
          placeholder={values.length || leading ? '' : placeholder}
          onChange={(e) => {
            setRejected(false);
            // A separator typed by hand ends an address the same way Enter does.
            if (/[,;]/.test(e.target.value)) commit(e.target.value);
            else setTyped(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Tab') {
              if (!typed.trim()) return;
              e.preventDefault();
              commit(typed);
              return;
            }
            // Backspace on an empty box takes back the last chip, which is how
            // every mail client behaves and the only way to undo a paste quickly.
            if (e.key === 'Backspace' && !typed && values.length) onChange(values.slice(0, -1));
          }}
          onBlur={() => commit(typed)}
          onPaste={(e) => {
            const text = e.clipboardData.getData('text');
            if (!/[,;\n]/.test(text)) return;
            e.preventDefault();
            commit(`${typed}${text}`);
          }}
        />
      </div>
    </div>
  );
}

export interface RecipientLists {
  to: string[];
  cc: string[];
  bcc: string[];
}

export function RecipientFields({
  value,
  onChange,
  /** Forward opens with an empty To, so that is the field wanting the caret. */
  focus,
  editableTo = true,
  leadingTo,
  subject,
  hideCopies = false,
}: {
  value: RecipientLists;
  onChange: (next: RecipientLists) => void;
  focus?: keyof RecipientLists;
  editableTo?: boolean;
  /** Draw To (and Subject) only. The new-message window folds the copy lines
   *  away until asked for, so a message nobody is copied on shows two rows. */
  hideCopies?: boolean;
  /** Drawn as the first To chip, and not this component's to remove — see Field. */
  leadingTo?: ReactNode;
  /**
   * A Subject row inside the same box, last. Optional because only one caller
   * has one: an email header reads as ONE block — To, Cc, Bcc, Subject — and a
   * subject in a rounded field of its own below reads as part of the body.
   */
  subject?: { value: string; onChange: (next: string) => void; placeholder?: string };
}) {
  return (
    <div className="cmp-rcpt">
      {editableTo && (
        <Field
          label="To"
          values={value.to}
          autoFocus={focus === 'to'}
          placeholder="Who this goes to"
          leading={leadingTo}
          onChange={(to) => onChange({ ...value, to })}
        />
      )}
      {!hideCopies && (
        <>
          <Field
            label="Cc"
            values={value.cc}
            autoFocus={focus === 'cc'}
            placeholder="Copy someone in"
            onChange={(cc) => onChange({ ...value, cc })}
          />
          <Field
            label="Bcc"
            values={value.bcc}
            autoFocus={focus === 'bcc'}
            placeholder="Blind copy"
            onChange={(bcc) => onChange({ ...value, bcc })}
          />
        </>
      )}
      {subject && (
        <div className="cmp-rcpt-field cmp-rcpt-inline-subject">
          <span className="cmp-rcpt-label">Subject</span>
          <input
            className="cmp-rcpt-subject-input"
            value={subject.value}
            onChange={(e) => subject.onChange(e.target.value)}
            placeholder={subject.placeholder ?? 'Subject'}
          />
        </div>
      )}
    </div>
  );
}
